import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so the same PIN typed twice (or set by OCR after a
// manual edit) doesn't fire the API again within the page lifetime.
// Phase 16.2 — added per user spec.
const _pincodeCache = new Map();

/**
 * PIN code input that auto-fetches city + state when the user types a valid
 * 6-digit code OR when the `value` prop changes externally (e.g. OCR fill).
 *
 * Backed by /api/lookup/pincode/{pin} which proxies postalpincode.in.
 *
 * Usage:
 *   <PincodeInput
 *     value={form.pincode}
 *     onChange={(v) => setForm({...form, pincode: v})}
 *     onResolve={({city, state, district, country}) => …}
 *   />
 *
 * Resolution callback fires on every successful lookup. If the API returns
 * 404 / errors, `error` is shown inline and `onResolve` is NOT called — the
 * agent can manually fill city + state (per Phase 16.2 spec).
 */
export default function PincodeInput({
  value = '',
  onChange,
  onResolve,
  className = 'h-12 rounded-xl',
  placeholder = '6-digit PIN',
  testId = 'input-pincode',
  inputProps = {},
  withCredentials = true,
}) {
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState('');
  // Track the last PIN we actually looked up so external value changes
  // don't endlessly re-trigger the lookup on every keystroke.
  const lastLookedUpRef = useRef('');

  const lookup = useCallback(async (pin) => {
    if (!pin || pin.length !== 6) return;
    if (lastLookedUpRef.current === pin) return;
    lastLookedUpRef.current = pin;
    // Serve from cache when available — no network roundtrip, instant UI feedback.
    if (_pincodeCache.has(pin)) {
      const cached = _pincodeCache.get(pin);
      if (cached === null) {
        setError('PIN not found');
        setResolved(false);
        return;
      }
      onResolve?.(cached);
      setResolved(true);
      setError('');
      return;
    }
    setBusy(true); setError(''); setResolved(false);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/lookup/pincode/${pin}`, withCredentials ? { withCredentials: true } : {});
      _pincodeCache.set(pin, r.data);
      onResolve?.(r.data);
      setResolved(true);
    } catch (err) {
      // Cache the negative so subsequent typing of the same bad PIN doesn't
      // hammer the upstream API.
      _pincodeCache.set(pin, null);
      const msg = err?.response?.data?.detail || 'Could not look up PIN';
      setError(typeof msg === 'string' ? msg : 'Lookup failed — enter city/state manually');
    } finally {
      setBusy(false);
    }
  }, [onResolve, withCredentials]);

  const handleChange = (e) => {
    // strip non-digits, cap to 6
    const v = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
    onChange?.(v);
    setResolved(false);
    setError('');
    if (v.length === 6) lookup(v);
  };

  const handleBlur = () => {
    if ((value || '').length === 6 && !resolved && !busy) lookup(value);
  };

  // Phase 16.2 — trigger lookup when `value` is set externally (OCR auto-fill,
  // form re-hydration after navigation). Without this useEffect, the OCR
  // workflow would write the PIN into the form but never resolve city/state.
  useEffect(() => {
    if (!value || value.length !== 6) return;
    if (lastLookedUpRef.current === value) return;
    lookup(value);
  }, [value, lookup]);

  return (
    <div className="space-y-1">
      <div className="relative">
        <Input
          {...inputProps}
          inputMode="numeric"
          maxLength={6}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`pr-10 ${className}`}
          data-testid={testId}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          {busy
            ? <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
            : <MapPin className={`w-4 h-4 ${resolved ? 'text-emerald-600' : 'text-slate-400'}`} />}
        </div>
      </div>
      {error && <p className="text-xs text-red-600" data-testid={`${testId}-error`}>{error} · Enter manually</p>}
      {resolved && !error && <p className="text-xs text-emerald-600" data-testid={`${testId}-success`}>City &amp; state filled automatically</p>}
    </div>
  );
}
